import React, { useState } from 'react';
import Modal from 'react-modal';
import type { TTimesheet } from '../../../types/timesheet';

interface RejectionModalProps {
    timesheet: TTimesheet;
    onClose: () => void;
    onSubmit: (reason: string) => void;
    isSubmitting?: boolean;
}

const RejectionModal: React.FC<RejectionModalProps> = ({
    timesheet,
    onClose,
    onSubmit,
    isSubmitting = false,
}) => {
    const [reason, setReason] = useState('');
    const [error, setError] = useState('');

    const minLength = 10;
    const maxLength = 500;
    const trimmedLength = reason.trim().length;

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        if (trimmedLength < minLength) {
            setError(`Please provide at least ${minLength} characters explaining the rejection.`);
            return;
        }

        if (trimmedLength > maxLength) {
            setError(`Rejection reason cannot exceed ${maxLength} characters.`);
            return;
        }

        setError('');
        onSubmit(reason.trim());
    };

    const handleReasonChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setReason(e.target.value);
        if (error) {
            setError('');
        }
    };

    return (
        <Modal
            className="rejection-modal"
            isOpen={true}
            onRequestClose={onClose}
            contentLabel={`Reject Timesheet #${timesheet.timesheet_number}`}
            ariaHideApp={false}
        >
            <form onSubmit={handleSubmit}>
                <div className="modal-body">
                    <div className="timesheet-info">
                        <p>
                            <strong>Worker:</strong> {timesheet.freelancer_name || `ID: ${timesheet.freelancer}`}
                        </p>
                        <p>
                            <strong>Date:</strong> {timesheet.timesheet_date}
                        </p>
                        <p>
                            <strong>Job:</strong> {timesheet.job_description}
                        </p>
                    </div>

                    <p className="rejection-modal__intro">
                        Please can you confirm why this timesheet is being declined? Our consultants will be in touch with you directly to discuss.
                    </p>

                    <div className="form-group">
                        <label htmlFor="rejection-reason">
                            Rejection Reason <span className="required">*</span>
                        </label>
                        <textarea
                            id="rejection-reason"
                            className={`form-control ${error ? 'is-invalid' : ''}`}
                            value={reason}
                            onChange={handleReasonChange}
                            placeholder="Please explain why you are rejecting this timesheet..."
                            rows={5}
                            disabled={isSubmitting}
                            maxLength={maxLength}
                            required={true}
                        />
                        <div className="char-count">
                            {reason.length} / {maxLength} characters
                            {trimmedLength < minLength && (
                                <span className="min-length-hint">
                                    {' '}
                                    (min. {minLength})
                                </span>
                            )}
                        </div>
                        {error && <div className="invalid-feedback">{error}</div>}
                    </div>
                </div>

                <div className="modal-bottom-bar">
                    <button
                        type="submit"
                        className="table-action-button table-action-button-danger"
                        disabled={isSubmitting || trimmedLength < minLength}
                    >
                        {isSubmitting ? 'Rejecting...' : 'Reject Timesheet'}
                    </button>
                    <button
                        type="button"
                        className="table-action-button table-action-button-secondary"
                        onClick={onClose}
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                </div>
            </form>
        </Modal>
    );
};

export default RejectionModal;
